import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ErrorState,
    LoadingState,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbArrowLeft } from "react-icons/tb";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import z from "zod";

import { Link as AppLink } from "@/components/link";
import { useGetIncidentAnalytics } from "@/queries/incidents.queries";
import { INCIDENT_REPORT_STATUS } from "@/types/incident.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import { formatIncidentAnalyticsCount } from "./utils/format-analytics-count";
import { mergeIncidentTypes } from "./utils/merge-incident-types";

const IncidentAnalyticsSearchSchema = z.object({
    status: z.nativeEnum(INCIDENT_REPORT_STATUS).optional(),
});

export type IncidentAnalyticsSearch = z.infer<typeof IncidentAnalyticsSearchSchema>;

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_INCIDENT_ANALYTICS)({
    component: IncidentAnalyticsPage,
    validateSearch: searchValidator(IncidentAnalyticsSearchSchema),
});

const ALL_STATUSES_VALUE = "all";

const CHART_COLORS = [
    "var(--chart-1)",
    "var(--chart-2)",
    "var(--chart-3)",
    "var(--chart-4)",
    "var(--chart-5)",
] as const;

const TOP_TYPES_CHART_LIMIT = 10;
const TYPE_LABEL_MAX_LENGTH = 22;

function truncateTypeLabel(label: string): string {
    if (label.length <= TYPE_LABEL_MAX_LENGTH) {
        return label;
    }
    return `${label.slice(0, TYPE_LABEL_MAX_LENGTH - 1)}…`;
}

function PeriodTooltip({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: { value: number; payload: { countExceededMax: boolean } }[];
    label?: string;
}) {
    if (!active || !payload?.length) return null;

    const item = payload[0];
    return (
        <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-md">
            <p className="font-medium">{label}</p>
            <p className="text-muted-foreground">
                {formatIncidentAnalyticsCount(item.value, item.payload.countExceededMax)} incidents
            </p>
        </div>
    );
}

function TypeTooltip({
    active,
    payload,
}: {
    active?: boolean;
    payload?: { name: string; value: number; payload: { name: string; countExceededMax: boolean } }[];
}) {
    if (!active || !payload?.length) return null;

    const item = payload[0];
    const label = item.payload.name ?? item.name;
    return (
        <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-md">
            <p className="font-medium">{label}</p>
            <p className="text-muted-foreground">
                {formatIncidentAnalyticsCount(item.value, item.payload.countExceededMax)} incidents
            </p>
        </div>
    );
}

function IncidentAnalyticsPage() {
    const { t } = useTranslation();
    const search = Route.useSearch({ structuralSharing: true });
    const navigate = Route.useNavigate();

    const statusFilter = search.status;
    const analyticsFilters = useMemo(() => (statusFilter ? { status: statusFilter } : {}), [statusFilter]);

    const { data, isLoading, isError } = useGetIncidentAnalytics(analyticsFilters);

    const updateStatusFilter = useCallback(
        (value: string) => {
            navigate({
                to: APP_ROUTES_TO.DEVELOPER_INCIDENT_ANALYTICS,
                search: (prev) => ({
                    ...prev,
                    status: value === ALL_STATUSES_VALUE ? undefined : (value as INCIDENT_REPORT_STATUS),
                }),
                replace: true,
            });
        },
        [navigate]
    );

    const mergedTypes = useMemo(() => mergeIncidentTypes(data?.byType ?? []), [data?.byType]);

    const typeChartData = useMemo(
        () =>
            mergedTypes.slice(0, TOP_TYPES_CHART_LIMIT).map((entry) => ({
                name: entry.type,
                count: entry.count,
                countExceededMax: entry.countExceededMax,
            })),
        [mergedTypes]
    );

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex flex-col gap-2">
                    <AppLink
                        to={APP_ROUTES_TO.INCIDENTS}
                        className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                    >
                        <TbArrowLeft className="h-4 w-4" />
                        {t("incidents")}
                    </AppLink>
                    <div>
                        <h1 className="text-2xl font-semibold">Incident analytics</h1>
                        <p className="text-muted-foreground">
                            Monthly and weekly incident volume with breakdown by type.
                        </p>
                    </div>
                </div>

                <Select value={statusFilter ?? ALL_STATUSES_VALUE} onValueChange={updateStatusFilter}>
                    <SelectTrigger className="w-48">
                        <SelectValue placeholder={t("incident_filter_status")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL_STATUSES_VALUE}>{t("all")}</SelectItem>
                        {Object.values(INCIDENT_REPORT_STATUS).map((status) => (
                            <SelectItem key={status} value={status}>
                                {t(`incident_status_${status.toLowerCase()}`)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {isLoading ? (
                <div className="flex min-h-[320px] items-center justify-center">
                    <LoadingState />
                </div>
            ) : isError ? (
                <ErrorState title="Failed to load incident analytics" />
            ) : (
                <>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base font-medium">
                                Total incidents
                                {statusFilter ? ` (${t(`incident_status_${statusFilter.toLowerCase()}`)})` : ""}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-semibold tabular-nums">
                                {formatIncidentAnalyticsCount(data?.total ?? 0, data?.totalExceededMax ?? false)}
                            </p>
                        </CardContent>
                    </Card>

                    <div className="grid gap-6 xl:grid-cols-2">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base font-medium">Incidents per month</CardTitle>
                            </CardHeader>
                            <CardContent className="h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={data?.byMonth ?? []}
                                        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" />
                                        <XAxis
                                            dataKey="label"
                                            tick={{ fontSize: 12 }}
                                            interval={0}
                                            angle={-35}
                                            textAnchor="end"
                                            height={70}
                                        />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={32} />
                                        <Tooltip content={<PeriodTooltip />} />
                                        <Bar dataKey="count" fill="var(--chart-1)" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base font-medium">Incidents per week</CardTitle>
                            </CardHeader>
                            <CardContent className="h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={data?.byWeek ?? []}
                                        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" />
                                        <XAxis
                                            dataKey="label"
                                            tick={{ fontSize: 12 }}
                                            interval={0}
                                            angle={-35}
                                            textAnchor="end"
                                            height={70}
                                        />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={32} />
                                        <Tooltip content={<PeriodTooltip />} />
                                        <Bar dataKey="count" fill="var(--chart-2)" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base font-medium">Top incident types</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {typeChartData.length === 0 ? (
                                    <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                                        No incident types yet
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={Math.max(typeChartData.length * 40, 160)}>
                                        <BarChart
                                            data={typeChartData}
                                            layout="vertical"
                                            margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
                                        >
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                className="stroke-border/60"
                                                horizontal={false}
                                            />
                                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                            <YAxis
                                                type="category"
                                                dataKey="name"
                                                width={148}
                                                tick={{ fontSize: 12 }}
                                                tickFormatter={truncateTypeLabel}
                                            />
                                            <Tooltip content={<TypeTooltip />} />
                                            <Bar dataKey="count" fill="var(--chart-1)" radius={[0, 4, 4, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base font-medium">All types</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {mergedTypes.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">No incident types yet</p>
                                ) : (
                                    <ul className="max-h-96 divide-y divide-border/60 overflow-y-auto">
                                        {mergedTypes.map((entry, index) => (
                                            <li
                                                key={entry.type}
                                                className="flex items-center justify-between py-3 text-sm"
                                            >
                                                <div className="flex min-w-0 items-center gap-2">
                                                    <span
                                                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                                                        style={{
                                                            backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                                                        }}
                                                    />
                                                    <span className="truncate" title={entry.type}>
                                                        {entry.type}
                                                    </span>
                                                </div>
                                                <span className="shrink-0 pl-3 font-medium tabular-nums">
                                                    {formatIncidentAnalyticsCount(entry.count, entry.countExceededMax)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
        </div>
    );
}
